import { AutomationRuleActionsDataEnum } from 'modules/automation-rules/actions/enums/action-enum'
import { getShouldFetchActions } from 'modules/automation-rules/actions/utils/get-should-fetch-actions'
import { AUTOMATION_RULES_FILTER_API } from 'modules/automation-rules/api/automation-rules-api'
import {
  FilterBaseApiInterface,
  FilterEmailApiInterface,
} from 'modules/automation-rules/types/automation-rule-filter-interface'
import { AutomationRuleBaseDataInterface } from 'modules/automation-rules/types/automation-rule-interface'
import {
  geDataFromApiData,
  getData,
  getPipelineData,
} from 'modules/automation-rules/utils/get-filter-data'
import { useCourseBundlesListAll } from 'modules/courses/bundles/hooks/use-course-bundlies-list-all'
import { coursesDependencies } from 'modules/courses/constants/courses-dependencies'
import { usePipelineList } from 'modules/crm/pipeline/hooks/use-pipeline-list'
import { useSmsTemplatesListAll } from 'modules/sms/hooks/use-sms-templates-list-all'
import { SmsTemplateListAllInterface } from 'modules/sms/types'
import { useTagsAll } from 'modules/tags/hooks/use-tags-all'
import { TagListAllInterface } from 'modules/tags/types/tag-interface'
import { Dispatch, SetStateAction, useState } from 'react'
import { useControlledSwr } from 'shared/hooks/use-controlled-swr'
import { useFetch } from 'shared/hooks/use-fetch'
import { RequestMethodsEnum } from 'shared/hooks/use-fetch/types'
import useUser from 'shared/hooks/use-user'
import useSWR, { KeyedMutator } from 'swr'
import { useCampaignsList } from 'modules/campaigns/hooks/use-campaigns-list'

export interface ActionsDataInterface {
  campaignData?: AutomationRuleBaseDataInterface[]
  tagData?: AutomationRuleBaseDataInterface[]
  communitiesData?: AutomationRuleBaseDataInterface[]
  emailData?: AutomationRuleBaseDataInterface[]
  specificEmailData?: AutomationRuleBaseDataInterface[]
  courseData?: AutomationRuleBaseDataInterface[]
  courseBundleData?: AutomationRuleBaseDataInterface[]
  pipelineData?: AutomationRuleBaseDataInterface[]
  smsTemplateData?: AutomationRuleBaseDataInterface[]
  mutateEmailData?: KeyedMutator<FilterEmailApiInterface[]>
  mutateSpecificEmailData?: KeyedMutator<FilterEmailApiInterface[]>
  mutateTagsData?: KeyedMutator<TagListAllInterface>
  mutateSmsTemplateData?: KeyedMutator<SmsTemplateListAllInterface>
}

export function useActionsData(): {
  data: ActionsDataInterface
  setShouldFetch: Dispatch<SetStateAction<Record<AutomationRuleActionsDataEnum, boolean>>>
} {
  const { user } = useUser()

  const [shouldFetch, setShouldFetch] = useState<Record<AutomationRuleActionsDataEnum, boolean>>(
    getShouldFetchActions(),
  )

  const { fetcher: baseFetcher } = useFetch<RequestMethodsEnum.get, FilterBaseApiInterface[]>({
    method: RequestMethodsEnum.get,
  })

  const { fetcher: emailFetcher } = useFetch<RequestMethodsEnum.get, FilterEmailApiInterface[]>({
    method: RequestMethodsEnum.get,
  })

  const { tags: tagsData, mutate: mutateTagsData } = useTagsAll(shouldFetch.tag)

  const { data: emailsData, mutate: mutateEmailData } = useSWR(() => {
    if (user && shouldFetch.mailing) {
      return `${AUTOMATION_RULES_FILTER_API}/action-type/email`
    }
    return null
  }, emailFetcher)

  const { data: specificEmailsData, mutate: mutateSpecificEmailData } = useSWR(() => {
    if (user && shouldFetch.specific_mailing) {
      return `${AUTOMATION_RULES_FILTER_API}/action-type/specific-email`
    }
    return null
  }, emailFetcher)

  const { data: coursesData } = useControlledSwr(
    () =>
      user &&
      shouldFetch.course && [
        `${AUTOMATION_RULES_FILTER_API}/action-type/course`,
        ...coursesDependencies,
      ],
    baseFetcher,
  )

  const { data: communitiesData } = useSWR(() => {
    if (user && shouldFetch.community) {
      return `${AUTOMATION_RULES_FILTER_API}/action-type/community`
    }
  }, baseFetcher)

  const { courseBundlesListAll } = useCourseBundlesListAll(shouldFetch?.course_bundle)

  const { campaignsList } = useCampaignsList(shouldFetch.campaign)

  const { pipelineStage } = usePipelineList(shouldFetch?.pipeline)

  const { data: smsTemplates, mutate: mutateSmsTemplateData } = useSmsTemplatesListAll(
    shouldFetch?.sms_template,
  )

  return {
    data: {
      campaignData: geDataFromApiData(campaignsList),
      tagData: getData(tagsData?.items),
      communitiesData: getData(communitiesData),
      emailData: emailsData?.map(el => ({ id: el.id, caption: el.subject })),
      specificEmailData: specificEmailsData?.map(el => ({ id: el.id, caption: el.subject })),
      courseData: getData(coursesData),
      courseBundleData: geDataFromApiData(courseBundlesListAll),
      pipelineData: getPipelineData(pipelineStage),
      smsTemplateData: getData(smsTemplates),
      mutateEmailData,
      mutateSpecificEmailData,
      mutateTagsData,
      mutateSmsTemplateData,
    },
    setShouldFetch,
  }
}
